import { useState } from "react";
import { Typography } from "components/atoms";
import { FetchError, NestedDropDown } from "components/molecules";
import {
  YouTubeIcon,
  SpiralIcon,
  PaperIcon,
  StudyPlannerContentNotExpandIcon,
  StudyPlannerPlusIcon,
  Spinner,
} from "components/atoms/Icon";
import { SmallSearchBar } from "components/molecules";
import {
  useAddStudyPlannerAdditionalItemMutation,
  useGetStudyPlannerAdditionalItemsQuery,
} from "services/studyPlanner";
import "./index.scss";
import { useLocation, useNavigate } from "react-router-dom";

interface IProps {
  theme?: "light" | "dark";
  date: string;
  closeModal?: () => void;
}

export const StudyPalnFilter = ({ theme, date, closeModal }: IProps) => {
  const [isFirstClicked, setIsFirstClicked] = useState(false);
  const [isSecondClicked, setIsSecondClicked] = useState(false);
  const [isThirdClicked, setIsThirdClicked] = useState(false);
  const [firstSearchText, setFirstSearchText] = useState("");
  const [isThirdNestedDropDownClicked, setIsThirdNestedDropDownClicked] =
    useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    isLoading,
    isFetching,
    refetch: refetchAdditionalItems,
    isError,
    data,
  } = useGetStudyPlannerAdditionalItemsQuery();
  const [
    AddStatus,
    { isLoading: isAddItemLoading, isError: isAddItemError },
  ] = useAddStudyPlannerAdditionalItemMutation();

  const handleExpand = (type: string) => {
    switch (type) {
      case "first":
        setIsFirstClicked((prev) => !prev);
        break;
      case "second":
        setIsSecondClicked((prev) => !prev);
        break;
      case "third":
        setIsThirdClicked((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const handleAddItem = async (itemId: string, itemDate: string) => {
    if (itemId && itemDate) {
      try {
        await AddStatus({ Id: itemId, PlannerDate: itemDate })
          .unwrap()
          .then(() => {
            refetchAdditionalItems();
            if (closeModal) {
              closeModal();
            } else if (location.pathname === "/add-to-study-plan") {
              navigate("/study-planner");
            }
          });
      } catch (error: any) {
        console.log("error : ", error);
      }
    }
  };

  return isLoading || isAddItemLoading || isFetching ? (
    <Spinner theme={theme} />
  ) : isError || isAddItemError ? (
    <FetchError content="Oops! failed to load resources." />
  ) : data ? (
    <div className="search-and-content-section">
      <div className="search-and-content-section-wrapper">
        <div className="search-bar-section">
          <SmallSearchBar text={firstSearchText} setText={setFirstSearchText} />
        </div>
        {firstSearchText !== "" ? (
          <div className="searched-exam-section">
            {data &&
              data.Items &&
              Array.isArray(data.Items) &&
              Object.entries(
                Object.assign(
                  {},
                  ...data.Items.flatMap((item: any) => [item.ContentItems])
                )
              )
                .filter((itemVal: any) =>
                  itemVal[1].toLowerCase().includes(firstSearchText.toLowerCase())
                )
                .map((itemArr: any, index: number) => {
                  return (
                    <div className="searched-exam-name-wrapper" key={index}>
                      <StudyPlannerPlusIcon
                        onClick={() => handleAddItem(itemArr[0], date)}
                      />
                      <Typography
                        label={itemArr[1]}
                        variant="h9Medium"
                        styles="typography-custom-style"
                        onClick={() => handleAddItem(itemArr[0], date)}
                      />
                    </div>
                  );
                })}
          </div>
        ) : (
          <div className="content-section">
            <div className="first-row">
              <div className="topic">
                <div className="icon-section">
                  <PaperIcon theme={theme} />
                </div>
                <div className="typography-section">
                  <Typography label="Full Length Exams" variant="h9Medium" />
                </div>
                <div className="expand-icon-section">
                  <StudyPlannerContentNotExpandIcon
                    onClick={handleExpand}
                    isExpand={isFirstClicked}
                    iconType="first"
                    theme={theme}
                  />
                </div>
              </div>
              {isFirstClicked && (
                <div className="exam-section">
                  {data &&
                    data.Items &&
                    Array.isArray(data.Items) &&
                    Object.entries(
                      data.Items.find((item: any) => item.Order === 1)
                        ?.ContentItems
                    ).map((itemArr: any, index: number) => (
                      <div className="exam-name-wrapper" key={index}>
                        <StudyPlannerPlusIcon
                          onClick={() => handleAddItem(itemArr[0], date)}
                        />
                        <Typography
                          label={itemArr[1]}
                          variant="h9Medium"
                          styles="typography-custom-style"
                          onClick={() => handleAddItem(itemArr[0], date)}
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="second-row">
              <div className="topic">
                <div className="icon-section">
                  <SpiralIcon theme={theme} />
                </div>
                <div className="typography-section">
                  <Typography label="AAMC Practice" variant="h9Medium" />
                </div>
                <div className="expand-icon-section">
                  <StudyPlannerContentNotExpandIcon
                    onClick={handleExpand}
                    isExpand={isSecondClicked}
                    iconType="second"
                    theme={theme}
                  />
                </div>
              </div>
              {isSecondClicked && (
                <div className="aamc-content-wrapper">
                  {data &&
                    data.Items &&
                    Array.isArray(data.Items) &&
                    Object.entries(
                      data.Items.find((item: any) => item.Order === 2)
                        ?.ContentItems
                    ).map((secondItemArr: any, index: number, arr: any) =>
                      arr.length === 0 ? (
                        <Typography label="(None)" variant="h9Book" />
                      ) : (
                        <div className="aamc-content" key={index}>
                          <StudyPlannerPlusIcon
                            onClick={() =>
                              handleAddItem(secondItemArr[0], date)
                            }
                          />
                          <Typography
                            label={secondItemArr[1]}
                            variant="h9Medium"
                            styles="typography-custom-style"
                            onClick={() => handleAddItem(secondItemArr[0], date)}
                          />
                        </div>
                      )
                    )}
                </div>
              )}
            </div>
            <div className="third-row">
              <div className="topic">
                <div className="icon-section">
                  <YouTubeIcon theme={theme} />
                </div>
                <div className="typography-section">
                  <Typography label="Medflix Videos" variant="h9Medium" />
                </div>
                <div className="expand-icon-section">
                  <StudyPlannerContentNotExpandIcon
                    onClick={handleExpand}
                    isExpand={isThirdClicked}
                    iconType="third"
                    theme={theme}
                  />
                </div>
              </div>

              {isThirdClicked && (
                <div className="content">
                  {data &&
                  data.ItemsGrouped &&
                  Array.isArray(data.ItemsGrouped) &&
                  data.ItemsGrouped.length > 0 &&
                  data.ItemsGrouped[0].ContentItemsGrouped &&
                  Array.isArray(data.ItemsGrouped[0].ContentItemsGrouped) &&
                  data.ItemsGrouped[0].ContentItemsGrouped.length > 0 ? (
                    <div className="search-section">
                      <NestedDropDown
                        setDropDownClicked={setIsThirdNestedDropDownClicked}
                        dropDownClicked={isThirdNestedDropDownClicked}
                        handleAdd={handleAddItem}
                        date={date}
                      />
                    </div>
                  ) : (
                    <Typography label="(None)" variant="h9Book" />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;
};
